import { createContext, useContext, type ReactNode } from 'react';
import { type Container, type Token } from './index';

const ContainerContext = createContext<Container | null>(null);

interface ContainerProviderProps {
  container: Container;
  children: ReactNode;
}

/**
 * Provides the DI container to the React tree.
 * Place this near the root of your application.
 */
export function ContainerProvider({ container, children }: ContainerProviderProps) {
  return <ContainerContext.Provider value={container}>{children}</ContainerContext.Provider>;
}

/**
 * Returns the DI container. Throws if used outside ContainerProvider.
 */
export function useContainer(): Container {
  const container = useContext(ContainerContext);
  if (!container) {
    throw new Error('useContainer must be called inside <ContainerProvider>');
  }
  return container;
}

/**
 * Resolves a service from the DI container by token.
 *
 * @example
 * const analytics = useInject(TOKENS.Analytics);
 * analytics.track('button_clicked');
 */
export function useInject<T>(token: Token<T>): T {
  return useContainer().inject(token);
}
